import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";

export default function ChangePassword() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const backHref =
        user?.role === "admin"
            ? "/admin/overview"
            : user?.role === "doctor"
              ? "/doctor/dashboard"
              : "/";

    async function submit(e) {
        e.preventDefault();
        setErr("");
        if (newPw.length < 6) {
            setErr("New password must be at least 6 characters.");
            return;
        }
        if (newPw !== confirmPw) {
            setErr("New password and confirmation do not match.");
            return;
        }
        setLoading(true);
        try {
            await api.put("/auth/password", {
                current_password: currentPw,
                new_password: newPw,
            });
            toast.success("Password updated.");
            setCurrentPw("");
            setNewPw("");
            setConfirmPw("");
            nav(backHref);
        } catch (e2) {
            const detail = e2?.response?.data?.detail;
            setErr(typeof detail === "string" ? detail : "Could not update password.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="veda-container-narrow py-14 md:py-24">
            <div className="mx-auto max-w-md">
                <Link
                    to={backHref}
                    className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                >
                    <ArrowLeft size={14} /> Back
                </Link>

                <div className="mt-6 flex items-center gap-2">
                    <span className="grid h-9 w-9 place-items-center bg-veda-brand/10 text-veda-brand">
                        <KeyRound size={16} />
                    </span>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                        Account
                    </p>
                </div>
                <h1 className="font-display mt-2 text-3xl text-veda-onSurface md:text-4xl">
                    Change password
                </h1>
                <p className="mt-2 text-sm text-veda-muted">
                    Signed in as{" "}
                    <span className="text-veda-onSurface">{user?.email}</span>{" "}
                    · {user?.role}
                </p>

                <form
                    onSubmit={submit}
                    data-testid={TID.changePasswordForm}
                    className="mt-6 flex flex-col gap-3"
                >
                    <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        Current password
                    </label>
                    <input
                        type="password"
                        autoComplete="current-password"
                        required
                        value={currentPw}
                        data-testid={TID.changePasswordCurrent}
                        onChange={(e) => setCurrentPw(e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                    />

                    <label className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        New password
                    </label>
                    <input
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={6}
                        value={newPw}
                        data-testid={TID.changePasswordNew}
                        onChange={(e) => setNewPw(e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                    />

                    <label className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        Confirm new password
                    </label>
                    <input
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={6}
                        value={confirmPw}
                        data-testid={TID.changePasswordConfirm}
                        onChange={(e) => setConfirmPw(e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                    />

                    {err && (
                        <p
                            data-testid={TID.changePasswordError}
                            className="text-xs text-veda-error"
                        >
                            {err}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid={TID.changePasswordSubmit}
                        className="mt-3 inline-flex items-center justify-center gap-2 bg-veda-brand px-4 py-2.5 text-sm font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-70"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        Update password
                    </button>
                </form>
            </div>
        </div>
    );
}
